import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
	Signal,
	signal,
	ViewChild,
} from '@angular/core';
import {
	IAuComment,
	IAuCommentEditEvent,
} from '@shared/ui-components/au-comments/i-au.comment';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { getCurrentUserPsId } from '@auth/state/auth.selectors';
import { Store } from '@ngrx/store';
import { AuRoundAvatarModule } from '@shared/ui-components';
import { MatIconModule } from '@angular/material/icon';
import { AuPipesModule } from '@app/shared/pipes/au-pipes.module';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { IRequester } from '@app/pages/cmms/interfaces/request.interfaces';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { assigneeList } from '@app/layout/state/layout.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { noWhitespaceValidator } from '@app/shared/validators/no-white-spaces.validator';
import { IMentionItem } from '@app/domain/types';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IAssigneeUser } from '@shared/interfaces/selectable-options.interfaces';

@Component({
	selector: 'comment-item',
	templateUrl: './comment-item.component.html',
	styleUrls: ['./comment-item.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AuRoundAvatarModule,
		MatIconModule,
		AuPipesModule,
		ReactiveFormsModule,
		CommonModule,
		MatMenuModule,
		MatButtonModule,
		NgxTolgeeModule,
		QuillEditorComponent,
		QuillModule,
		NgxTolgeeModule,
	],
})
export class CommentItemComponent implements OnInit {
	private readonly store$ = inject(Store);
	private userPsId = this.store$.selectSignal(getCurrentUserPsId);
	private readonly destroyRef = inject(DestroyRef);
	private readonly fb = inject(FormBuilder);

	@Input() comment: IAuComment;
	@Input() requester: IRequester[];

	autoGenerated = computed(() => this.comment.author.displayName === 'Autility System');
	@Output() deleteCommentPressed = new EventEmitter<string>();
	@Output() editCommentSubmitted = new EventEmitter<IAuCommentEditEvent>();

	@ViewChild(QuillEditorComponent, { static: true }) editor:
		| QuillEditorComponent
		| undefined;

	sentByMe: Signal<boolean>;
	canEdit: Signal<boolean>;
	inEditMode = signal(false);
	isEmail = computed(() => this.comment.sentAsEmail);
	assigneeList: IAssigneeUser[] = [];
	modules = {
		toolbar: false,
		mention: {
			allowedChars: /^[A-Za-z\sÅÄÖåäöÆØÅæøå]*$/,
			positioningStrategy: 'fixed',
			mentionContainerClass:
				'ql-mention-list-container nice-scroll visible-scroll colored-scroll-track',
			onSelect: (item: IMentionItem, insertItem: any) => {
				const editor = this.editor!.quillEditor;
				insertItem(item);
				editor.insertText(editor.getLength() - 1, '', 'user');
			},

			source: (searchTerm: string, renderList: any) => {
				const assigneePkAndName = this.assigneeList.map<IMentionItem>(assignee => ({
					value: `${assignee.firstName} ${assignee.lastName}`,
					id: assignee.psId,
				}));
				if (searchTerm.length === 0) {
					renderList(assigneePkAndName, searchTerm);
					return;
				}
				const matches: IMentionItem[] = assigneePkAndName.filter(assignee =>
					assignee.value.toLowerCase().includes(searchTerm.toLowerCase())
				);

				renderList(matches, searchTerm);
			},
		},
	};
	commentEditInputForm: FormGroup = this.fb.group({
		textArea: new FormControl<string>('', [Validators.required, noWhitespaceValidator()]),
	});
	formControl = this.commentEditInputForm.get('textArea');

	ngOnInit() {
		this.sentByMe = computed(() => this.userPsId() === this.comment.author?.psId);
		this.canEdit = computed(() => this.sentByMe() && !this.isEmail());
		this.formControl.setValue(this.comment.text, { emitEvent: false });
		if (!this.comment.author) {
			throw new Error('Comment author is null. Fallback is used!');
		}
		this.store$
			.select(assigneeList)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(assigneeList => {
				this.assigneeList = assigneeList.reduce((allUsers, org) => {
					return allUsers.concat(org.users);
				}, []);
			});
	}

	submitEditedText() {
		this.editCommentSubmitted.emit({
			commentId: this.comment.id,
			commentText: this.formControl.value,
		});
		this.inEditMode.set(false);
	}

	deleteComment() {
		this.deleteCommentPressed.emit(this.comment.id);
	}

	get commentAuthorAvatar(): string {
		const author = this.comment.author;

		if (!author) {
			return null;
		}

		return author.imageUrl;
	}

	get commentAuthorName(): string {
		const author = this.comment.author;
		if (!author) {
			return 'Anonymous';
		}

		return author.displayName;
	}

	isHtml(content: string): boolean {
		const regexForHTML = /^<([A-Za-z][A-Za-z0-9]*)\b[^>]*>/;
		return regexForHTML.test(content);
	}
}
